import React, { useState, useEffect, useMemo } from "react";
import { TabContainer, Tab, TabHeader, TabContent } from "./Styled";
import { useTabURLState } from "../../hooks/useTabURLState";

export interface TabBarProps {
  label: string;
  labelEN?: string;
  content: React.ReactNode;
}

interface TabNavigationProps {
  tabs: TabBarProps[];
  tabParamName?: string;
  defaultTab?: string;
}

const TabBar = ({ tabs, tabParamName = 'tab', defaultTab }: TabNavigationProps) => {
  const { currentTab, switchTab, updateTabParams } = useTabURLState({ 
    tabParamName, 
    defaultTab: defaultTab || (tabs[0]?.labelEN || '') 
  });

  // Memoize the active tab index to prevent unnecessary recalculations
  const activeTabIndex = useMemo(() => {
    const index = tabs.findIndex((tab) => tab.labelEN === currentTab);
    return index === -1 ? 0 : index; // Default to the first tab if not found
  }, [currentTab, tabs]);

  const [activeTab, setActiveTab] = useState(activeTabIndex);
  
  // Track which tabs have been loaded to implement lazy loading
  const [loadedTabs, setLoadedTabs] = useState<string[]>([currentTab]);

  // Only update active tab when it actually changes and is valid
  useEffect(() => {
    if (activeTabIndex !== activeTab && activeTabIndex >= 0) {
      setActiveTab(activeTabIndex);
      // Mark the new tab as loaded for lazy loading
      const newTabLabel = tabs[activeTabIndex]?.labelEN;
      if (newTabLabel && !loadedTabs.includes(newTabLabel)) {
        setLoadedTabs(prev => [...prev, newTabLabel]);
      }
    }
  }, [activeTabIndex, activeTab, tabs, loadedTabs]);

  const handleTabClick = (index: number) => {
    const newTab = tabs[index].labelEN;
    if (newTab) {
      switchTab(newTab);
      setActiveTab(index);
      // Mark the clicked tab as loaded for lazy loading
      if (!loadedTabs.includes(newTab)) {
        setLoadedTabs(prev => [...prev, newTab]);
      }
    }
  };

  return (
    <TabContainer>
      <TabHeader>
        {tabs.map((tab, index) => (
          <Tab
            key={tab.label}
            active={index === activeTab}
            onClick={() => handleTabClick(index)}
          >
            <div className="text">{tab.label}</div>
          </Tab>
        ))}
      </TabHeader>
      <div style={{ position: 'relative' }}>
        {tabs.map((tab, index) => {
          const isActive = index === activeTab;
          const isLoaded = loadedTabs.includes(tab.labelEN || '');
          
          // Only render content if tab is active or has been loaded before
          return (
            <TabContent
              key={tab.label}
              active={isActive}
              direction="right"
            >
              {isLoaded ? tab.content : null}
            </TabContent>
          );
        })}
      </div>
    </TabContainer>
  );
};

export default TabBar;
